import { createFileRoute, notFound } from "@tanstack/react-router"
import { SITE_URL } from "@/lib/constants/site-url"
import { jsonLd } from "@/lib/utils/json-ld"
import { TUS_FAMILY, TUS_GROUP_URL } from "@/lib/data/tus-family"
import AboutPage from "@/pages/about"

// First-party share card: the real +Bake dining room, cropped to 1200x630.
const OG_IMAGE = `${SITE_URL}/images/plusbake/optimized/og-about-1200x630.jpg`

export const Route = createFileRoute("/$countryCode/about")({
  loader: async ({ params, context }) => {
    const { countryCode } = params
    const { queryClient } = context
    const { getRegion } = await import("@/lib/data/regions")

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region) {
      throw notFound()
    }

    return { countryCode, region }
  },
  head: () => {
    const title = "Our Story | +Bake Cafe & the Take Ur Seat Family"
    const description =
      "Meet +Bake Cafe in Rancho Cucamonga and the Take Ur Seat family — sister restaurants in Chino Hills, Ontario, Pomona and Redlands, CA."
    const canonical = `${SITE_URL}/about`
    const businessId = `${SITE_URL}/#bakery`
    const groupId = `${TUS_GROUP_URL}/#organization`

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "About", item: canonical },
      ],
    }

    // One @graph describing the page and the corporate relationship.
    //
    // Deliberately NO LocalBusiness/Restaurant nodes for the sister locations.
    // Those businesses sit at four other addresses; declaring their NAP on
    // plusbake.com would hand search engines competing address signals for this
    // domain's own local entity, and would duplicate (not corroborate) the
    // schema each sister already publishes on its own site. The sisters appear
    // here only as Organization reference stubs — name + url, nothing more.
    //
    // The #bakery node is a PARTIAL re-statement, not a redeclaration: it
    // carries only the @id, identity, and the new parentOrganization edge.
    // Address, geo, hours and areaServed stay on "/" alone. Same-@id partials
    // merge, which is the point.
    const aboutGraph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "AboutPage",
          "@id": `${canonical}#webpage`,
          url: canonical,
          name: title,
          description,
          isPartOf: { "@id": `${SITE_URL}/#website` },
          mainEntity: { "@id": businessId },
          primaryImageOfPage: OG_IMAGE,
          inLanguage: "en-US",
        },
        {
          "@type": ["Bakery", "CafeOrCoffeeShop"],
          "@id": businessId,
          name: "+Bake Cafe",
          url: SITE_URL,
          foundingDate: "2022",
          parentOrganization: { "@id": groupId },
        },
        {
          "@type": "Organization",
          "@id": groupId,
          name: "Take Ur Seat",
          alternateName: "Take Ur Seat Restaurant Group",
          url: TUS_GROUP_URL,
          subOrganization: [
            { "@id": businessId },
            ...TUS_FAMILY.map((brand) => ({
              "@type": "Organization",
              name: `${brand.name} — ${brand.city}, ${brand.region}`,
              url: brand.url,
            })),
          ],
        },
      ],
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:site_name", content: "+Bake Cafe" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
        { type: "application/ld+json", children: jsonLd(aboutGraph) },
      ],
    }
  },
  component: AboutPage,
})
